import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import "./Home.scss";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import { getHomeActivityRoute, getHomeTemplatesRoute } from "../../api/routes";
import { getShortDateString, getShortDateTimeString } from "../../helpers/Dates";
import { IconJsxer } from "../../helpers/IconHelper";
import PageTopBanner from "../../components/PageTopBanner/PageTopBanner";
import config from "../../config";
import toDisplayAssetUrl from "../../helpers/DisplayAssetUrl";
import useStationaryTooltip from "../../hooks/useStationaryTooltip";
import TemplateTooltip from "../../components/TemplateTooltip/TemplateTooltip";
import { assets } from "../../assets/assets";

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

const getHomeCardImageUrl = (url) => url ? toDisplayAssetUrl(url) : assets.icastFrontPageLogo;
const getStreamerAvatarUrl = (channel) => channel?.profilePic || assets.BlankUser;
const getUpcomingStreamers = (stream) => {
    const channels = Array.isArray(stream.channels) ? stream.channels.filter((channel) => channel?.twitchHandle || channel?.profilePic) : [];

    if (channels.length > 0) {
        return channels;
    }

    if (stream.twitchHandle) {
        return [{ twitchHandle: stream.twitchHandle, profilePic: '' }];
    }

    return [];
};

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const isNewTemplate = (t) => Boolean(t.createdAt) && Date.now() - new Date(t.createdAt).getTime() < ONE_WEEK_MS;

// ─── Template card for the Quickstart strip ────────────────────────────────
const QuickstartCard = ({ template, onClick }) => {
    const { tooltipVisible, anchorRef, onMouseEnter, onMouseMove, onMouseLeave } = useStationaryTooltip();
    return (
        <div
            ref={anchorRef}
            className="qs-card button-active"
            onClick={() => onClick(template)}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <div
                className="qs-card-image"
                style={template.templateImage ? { backgroundImage: `url(${toDisplayUrl(template.templateImage)})` } : {}}
            >
                {(isNewTemplate(template) || template.templateType === 'campaign') && (
                    <div className="tpl-badges">
                        {isNewTemplate(template) && <span className="tpl-badge tpl-badge--new">NEW</span>}
                        {template.templateType === 'campaign' && <span className="tpl-badge tpl-badge--campaign">CAMPAIGN</span>}
                    </div>
                )}
            </div>
            <div className="qs-card-label">
                <span className="qs-card-title">{template.name}</span>
                <span className="qs-card-sub">{template.featureType}</span>
            </div>
            <TemplateTooltip
                anchorRef={anchorRef}
                visible={tooltipVisible}
                title={template.name}
                description={template.description}
            />
        </div>
    );
};

// ─── Template card for the "Get Inspired" grid ────────────────────────────
const TemplateCard = ({ template, showCampaignBadge = true, onClick }) => {
    const isNew = isNewTemplate(template);
    const { tooltipVisible, anchorRef, onMouseEnter, onMouseMove, onMouseLeave } = useStationaryTooltip();
    return (
        <div
            ref={anchorRef}
            className="tpl-card button-active"
            onClick={() => onClick(template)}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <div
                className="tpl-card-image"
                style={template.templateImage ? { backgroundImage: `url(${toDisplayAssetUrl(template.templateImage)})` } : {}}
            >
                {(isNew || (showCampaignBadge && template.templateType === 'campaign')) && (
                    <div className="tpl-badges">
                        {isNew && <span className="tpl-badge tpl-badge--new">NEW</span>}
                        {showCampaignBadge && template.templateType === 'campaign' && <span className="tpl-badge tpl-badge--campaign">CAMPAIGN</span>}
                    </div>
                )}
            </div>
            <div className="tpl-card-info">
                <span className="tpl-card-title">{template.name || 'Feature Title'}</span>
                <span className="tpl-card-sub">{template.featureType || 'Project Title'}</span>
            </div>
            <TemplateTooltip
                anchorRef={anchorRef}
                visible={tooltipVisible}
                title={template.name}
                description={template.description}
            />
        </div>
    );
};

// ─── Recently modified campaign card ──────────────────────────────────────
const RecentCampaignCard = ({ campaign, onClick }) => (
    <div className="activity-card recent-campaign-card button-active" onClick={() => onClick(campaign._id)}>
        <div
            className={`activity-card-media${campaign.campaignImage ? '' : ' is-fallback'}`}
            style={{ backgroundImage: `url(${getHomeCardImageUrl(campaign.campaignImage)})` }}
        />
        <div className="activity-card-content recent-campaign-content">
            <div className="activity-card-title">{campaign.campaignName}</div>
            <div className="activity-card-date">{campaign.lastModified ? getShortDateString(campaign.lastModified) : '—'}</div>
        </div>
    </div>
);

// ─── Upcoming stream card ─────────────────────────────────────────────────
const UpcomingStreamCard = ({ stream, onClick }) => {
    const live = stream.status === 'live';
    const streamers = getUpcomingStreamers(stream);
    const tzAbbr = stream.timeZone
        ? (new Intl.DateTimeFormat('en', { timeZoneName: 'short', timeZone: stream.timeZone })
            .formatToParts(new Date())
            .find(p => p.type === 'timeZoneName')?.value || '')
        : '';
    return (
        <div className="activity-card stream-card button-active" onClick={() => onClick(stream)}>
            <div
                className={`activity-card-media${stream.campaignImage ? '' : ' is-fallback'}`}
                style={{ backgroundImage: `url(${getHomeCardImageUrl(stream.campaignImage)})` }}
            />
            <div className="activity-card-content">
                <div className="activity-card-title">{stream.campaignName}</div>
                <div className="activity-card-sub">{stream.streamName || stream.twitchHandle}</div>
                <div className="activity-card-date">
                    {stream.startTime ? getShortDateTimeString(stream.startTime) : '—'}
                    {stream.endTime ? ` – ${getShortDateTimeString(stream.endTime)}` : ''}
                </div>
                <div className="activity-card-meta-row">
                    {tzAbbr && <span className="stream-tz-chip">{tzAbbr}</span>}
                    {live && <span className="stream-live-pill">LIVE</span>}
                </div>
                {streamers.length > 0 && (
                    <div className="activity-card-streamers">
                        {streamers.slice(0, 4).map((channel, index) => (
                            <div key={`${channel.twitchHandle || 'streamer'}-${index}`} className="streamer-chip">
                                <img
                                    className="streamer-avatar"
                                    src={getStreamerAvatarUrl(channel)}
                                    alt={channel.twitchHandle || 'Streamer avatar'}
                                />
                                <span>{channel.twitchHandle || 'Streamer'}</span>
                            </div>
                        ))}
                        {streamers.length > 4 && <div className="streamer-chip streamer-chip-more">+{streamers.length - 4}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Analytics summary card ───────────────────────────────────────────────
const AnalyticsCard = ({ entry, onClick }) => {
    let totalInteractions = 0;
    let uniqueUsers = 0;
    entry.streams.forEach(s =>
        s.recordedAnalytics.forEach(r => {
            if (!r.disabled) {
                totalInteractions += r.interactions || 0;
                uniqueUsers += r.uniqueUsers || 0;
            }
        })
    );
    return (
        <div className="activity-card analytics-card button-active" onClick={() => onClick(entry)}>
            <div
                className={`activity-card-media${entry.campaignImage ? '' : ' is-fallback'}`}
                style={{ backgroundImage: `url(${getHomeCardImageUrl(entry.campaignImage)})` }}
            />
            <div className="activity-card-content analytics-card-content">
                <div className="activity-card-title">{entry.campaignName}</div>
                <div className="activity-card-sub">{entry.client}</div>
                {entry.latestStreamTime > 0 && (
                    <div className="activity-card-date">{getShortDateString(entry.latestStreamTime)}</div>
                )}
                <div className="activity-card-stats">
                    <div className="analytics-stat-row">
                        <span className="analytics-stat-label">Total interactions</span>
                        <span className="analytics-stat-value">{totalInteractions.toLocaleString()}</span>
                    </div>
                    <div className="analytics-stat-row">
                        <span className="analytics-stat-label">Unique users</span>
                        <span className="analytics-stat-value">{uniqueUsers.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Section header with "View all" link ─────────────────────────────────
const SectionHeader = ({ title, onViewAll }) => (
    <div className="home-section-header">
        <h5>{title}</h5>
        {onViewAll && (
            <button className="view-all-btn" onClick={onViewAll}>View all</button>
        )}
    </div>
);

// ─── Main Home component ──────────────────────────────────────────────────
const Home = () => {
    const navigate = useNavigate();

    const [quickstartTemplates, setQuickstartTemplates] = useState([]);
    const [featuredTemplates, setFeaturedTemplates] = useState([]);
    const [recentCampaigns, setRecentCampaigns] = useState([]);
    const [upcomingStreams, setUpcomingStreams] = useState([]);
    const [recentAnalytics, setRecentAnalytics] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(true);

    useEffect(() => {
        axios.get(getHomeActivityRoute, { withCredentials: true })
            .then(res => {
                setRecentCampaigns(res.data.recentCampaigns || []);
                setUpcomingStreams(res.data.upcomingStreams || []);
                setRecentAnalytics(res.data.recentAnalytics || []);
            })
            .catch(() => toast.error('Failed to load activity data'))
            .finally(() => setLoadingActivity(false));

        axios.get(getHomeTemplatesRoute, { withCredentials: true })
            .then(res => {
                setQuickstartTemplates(res.data.data?.quickstartTemplates || []);
                setFeaturedTemplates(res.data.data?.featuredTemplates || []);
            })
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoadingTemplates(false));
    }, []);

    const openCampaign = (campaignId) =>
        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_OVERVIEW.replace(':id', campaignId));

    const openCampaignStream = (stream) =>
        navigate(
            TOP_LEVEL_NAV_ROUTES.MANAGE_CAMPAIGN_STREAM
                .replace(':campaignId', stream.campaignId)
                .replace(':streamId', stream.streamId)
        );

    const openAnalytics = (entry) =>
        navigate(
            TOP_LEVEL_NAV_ROUTES.CAMPAIGN_OVERVIEW.replace(':id', entry.campaignId),
            { state: { openAnalyticsDropdown: true } }
        );

    const startFromTemplate = (template) =>
        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_SETUP, {
            state: {
                fromInspiration: true,
                templateId: template._id || template.id,
                templateType: template.templateType,
            },
        });

    const featuredFeatureTemplates = featuredTemplates.filter(t => t.templateType === 'feature');
    const featuredCampaignTemplates = featuredTemplates.filter(t => t.templateType === 'campaign');

    const hasActivity = recentCampaigns.length > 0 || upcomingStreams.length > 0 || recentAnalytics.length > 0;

    return (
        <div className="home-page content-page-container">
            <PageTopBanner />

            {/* ── Campaign Quick Start ── */}
            <div className="home-section home-quickstart-section">
                <div className="home-section-inner">
                    <div className="fl-row-slim home-quickstart-header">
                        <h4 className="page-title">Campaign Quick Start</h4>
                        <div className="heading-button-container">
                            <div
                                className="standard-button heading-slim-button button-active"
                                onClick={() => navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_SETUP)}
                            >
                                <div className="fl-row">
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.plus, IconJsxer.ICON_STYLES.roundPanelButtonSmall)}
                                    <div>Create Campaign</div>
                                </div>
                            </div>
                        </div>
                        <div className="heading-divider-line" />
                    </div>
                    <p className="home-quickstart-hint">
                        Jumpstart your campaign by selecting a starting feature – you can add more features once you're in your workspace.
                    </p>
                    {loadingTemplates ? (
                        <div className="home-loading-row" />
                    ) : quickstartTemplates.length > 0 ? (
                        <div className="qs-scroll-row">
                            {quickstartTemplates.map(t => (
                                <QuickstartCard key={t.id} template={t} onClick={startFromTemplate} />
                            ))}
                        </div>
                    ) : (
                        <div className="home-empty-state">No quickstart templates available.</div>
                    )}
                </div>
            </div>

            {/* ── Your Activity ── */}
            <div className="home-section home-activity-section">
                <div className="home-section-inner">
                    <div className="home-section-title-row">
                        <div className="home-section-title-block">
                            <div className="home-section-title-frame" />
                            <h4>Your Activity</h4>
                        </div>
                    </div>

                    {loadingActivity ? (
                        <div className="home-loading-placeholder" />
                    ) : !hasActivity ? (
                        <div className="home-empty-state">No recent activity found. Create a campaign to get started!</div>
                    ) : (
                        <>
                            {/* Last modified */}
                            {recentCampaigns.length > 0 && (
                                <div className="home-activity-group">
                                    <SectionHeader
                                        title="Last modified"
                                        onViewAll={() => navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGNS)}
                                    />
                                    <div className="activity-card-row">
                                        {recentCampaigns.map(c => (
                                            <RecentCampaignCard key={c._id} campaign={c} onClick={openCampaign} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upcoming streams */}
                            {upcomingStreams.length > 0 && (
                                <div className="home-activity-group">
                                    <SectionHeader
                                        title="Upcoming streams"
                                        onViewAll={() => navigate(TOP_LEVEL_NAV_ROUTES.SCHEDULE)}
                                    />
                                    <div className="activity-card-row">
                                        {upcomingStreams.map(s => (
                                            <UpcomingStreamCard key={s.streamId} stream={s} onClick={openCampaignStream} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Stream analytics */}
                            {recentAnalytics.length > 0 && (
                                <div className="home-activity-group">
                                    <SectionHeader
                                        title="Campaign analytics"
                                        onViewAll={() => navigate(TOP_LEVEL_NAV_ROUTES.ANALYTICS)}
                                    />
                                    <div className="activity-card-row">
                                        {recentAnalytics.map(entry => (
                                            <AnalyticsCard key={entry.campaignId} entry={entry} onClick={openAnalytics} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Get Inspired ── */}
            <div className="home-section home-inspired-section">
                <div className="home-section-inner">
                    <div className="home-section-title-row">
                        <div className="home-section-title-block">
                            <div className="home-section-title-frame" />
                            <h4>Get Inspired</h4>
                        </div>
                    </div>

                    {loadingTemplates ? (
                        <div className="home-loading-placeholder" />
                    ) : featuredTemplates.length === 0 ? (
                        <div className="home-empty-state">No featured templates available yet.</div>
                    ) : (
                        <>
                            {featuredFeatureTemplates.length > 0 && (
                                <div className="home-inspired-group">
                                    <SectionHeader
                                        title="Featured Templates"
                                        onViewAll={() => navigate(TOP_LEVEL_NAV_ROUTES.INSPIRATION, { state: { tab: 'feature' } })}
                                    />
                                    <div className="tpl-card-grid">
                                        {featuredFeatureTemplates.map(t => (
                                            <TemplateCard
                                                key={t.id}
                                                template={t}
                                                onClick={startFromTemplate}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {featuredCampaignTemplates.length > 0 && (
                                <div className="home-inspired-group">
                                    <SectionHeader
                                        title="Campaign Templates"
                                        onViewAll={() => navigate(TOP_LEVEL_NAV_ROUTES.INSPIRATION, { state: { tab: 'campaign' } })}
                                    />
                                    <div className="tpl-card-grid">
                                        {featuredCampaignTemplates.map(t => (
                                            <TemplateCard
                                                key={t.id}
                                                template={t}
                                                showCampaignBadge={false}
                                                onClick={startFromTemplate}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Home;
